import React, { FunctionComponent, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { XIcon } from '../../atoms/icons/XIcon'
import { PermissionsIllustration } from '../../atoms/illustrations/PermissionsIllustration'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { TextButton } from '../../atoms/textButton/TextButton'
import { Title } from '../../atoms/title/Title'
import { Modal } from '../../organisms/modal/Modal'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ProviderCalendarAuthorizationModalProps = {
  onAuthorizePressed?: () => void
  onCancelPressed?: () => void
  isVisible?: boolean
}

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  alignSelf: 'flex-start',
  gap: theme.spacing['48px'],
  alignItems: 'center',
}))

const Header = styled.View({
  flexDirection: 'column',
  alignItems: 'center',
})

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['16px'],
  alignSelf: 'flex-end',
  alignItems: 'center',
}))

const LinkButton = styled(TextButton)({
  alignSelf: 'center',
})

const HeaderText = styled(Title)(({ theme }) => ({
  marginTop: theme.spacing['24px'],
}))

const CloseButton = styled(PressableOpacity)({
  alignSelf: 'flex-end',
})

export const ProviderCalendarAuthorizationModal: FunctionComponent<ProviderCalendarAuthorizationModalProps> = ({
  onAuthorizePressed,
  onCancelPressed,
  isVisible = false,
}) => {
  const theme = useTheme() as ThemeType
  const ref = useRef<BottomSheetGorhom>(null)
  const openBottomSheet = useCallback(() => {
    ref.current?.expand()
  }, [])
  return (
    <Modal
      visible={isVisible}
      bottomSheetRef={ref}
      snapPoints={[550]}
      width='600px'
      onLayout={openBottomSheet}
      modalContents={
        <Container>
          <CloseButton hitSlop={60} onPress={() => onCancelPressed?.()}>
            <XIcon size={24} fillColor={theme.colors.iconDefault} />
          </CloseButton>
          <Header>
            <PermissionsIllustration />
            <HeaderText
              textAlign='center'
              size={TitleSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage='Login to your Google account to authorize access to your calendar'
                  description='Title for provider calendar authorize card'
                />
              }
            />
            <BodyText
              textAlign='center'
              color={theme.colors.textSecondary}
              text={
                <FormattedMessage
                  defaultMessage='We’ll be able to show you Google Calendar events on your Lyra calendar'
                  description='Body text for provider calendar authorize card'
                />
              }
            />
          </Header>
          <Row>
            <LinkButton
              text={
                <FormattedMessage defaultMessage='Cancel' description='Button to cancel authorization of calendar' />
              }
              onPress={() => onCancelPressed?.()}
            />
            <PrimaryButton
              testID={tID('ProviderCalendarAuthorizationModal-authorize-button')}
              text={<FormattedMessage defaultMessage='Authorize' description='Button to authorize google calendar' />}
              onPress={() => onAuthorizePressed?.()}
            />
          </Row>
        </Container>
      }
      onRequestClose={() => onCancelPressed?.()}
      onCloseEnd={() => onCancelPressed?.()}
    />
  )
}
